<template>
  <div class="class-filter-group">
    <label v-if="showLabel" class="class-filter-label">
      {{ label }}
      <span v-if="hint" class="class-filter-hint">{{ hint }}</span>
    </label>

    <div class="class-dropdown" v-click-outside="closeDropdown">
      <button type="button" class="class-dropdown-trigger" @click="toggleDropdown">
        <span class="selected-classes-text">{{ displayText }}</span>
        <i :class="['fas', dropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
      </button>

      <div v-if="dropdownOpen" class="class-dropdown-menu">
        <div
          v-for="classOption in options"
          :key="classOption"
          :class="['class-option', { selected: selectedValues.includes(classOption) }]"
          @click="toggleSelection(classOption)"
        >
          <span class="class-checkbox">
            <i v-if="selectedValues.includes(classOption)" class="fas fa-check"></i>
          </span>
          <span class="class-name">{{ classOption }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="selectedValues.length > 0 && !selectedValues.includes(allOptionLabel)"
      class="selected-classes-tags"
    >
      <span v-for="cls in selectedValues" :key="cls" class="class-tag">
        {{ cls }}
        <i class="fas fa-times" @click.stop="removeSelection(cls)"></i>
      </span>
    </div>
  </div>
</template>

<script>
import { ALL_CLASSES } from '@/utils/defectStatistics'

export default {
  name: 'ClassMultiSelectFilter',
  directives: {
    'click-outside': {
      bind (el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind (el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },
  props: {
    value: {
      type: Array,
      default: () => [ALL_CLASSES]
    },
    options: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: 'Por classe'
    },
    hint: {
      type: String,
      default: '(uma ou mais)'
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    allOptionLabel: {
      type: String,
      default: ALL_CLASSES
    },
    allSelectedText: {
      type: String,
      default: 'Todas as classes'
    },
    emptyText: {
      type: String,
      default: 'Selecione uma classe'
    }
  },
  data () {
    return {
      dropdownOpen: false
    }
  },
  computed: {
    selectedValues () {
      return this.value
    },
    displayText () {
      if (this.selectedValues.includes(this.allOptionLabel)) {
        return this.allSelectedText
      }
      if (this.selectedValues.length === 0) {
        return this.emptyText
      }
      if (this.selectedValues.length === 1) {
        return this.selectedValues[0]
      }
      return `${this.selectedValues.length} classes selecionadas`
    }
  },
  methods: {
    toggleDropdown () {
      this.dropdownOpen = !this.dropdownOpen
    },
    closeDropdown () {
      this.dropdownOpen = false
    },
    emitValue (nextValue) {
      this.$emit('input', nextValue)
      this.$emit('change', nextValue)
    },
    toggleSelection (classOption) {
      if (classOption === this.allOptionLabel) {
        this.emitValue([this.allOptionLabel])
        this.closeDropdown()
        return
      }

      let next = [...this.selectedValues]
      if (next.includes(this.allOptionLabel)) {
        next = []
      }

      const index = next.indexOf(classOption)
      if (index > -1) {
        next.splice(index, 1)
        if (next.length === 0) {
          next = [this.allOptionLabel]
        }
      } else {
        next.push(classOption)
      }

      this.emitValue(next)
    },
    removeSelection (classOption) {
      const next = [...this.selectedValues]
      const index = next.indexOf(classOption)
      if (index > -1) {
        next.splice(index, 1)
        if (next.length === 0) {
          next.push(this.allOptionLabel)
        }
        this.emitValue(next)
      }
    }
  }
}
</script>

<style scoped>
.class-filter-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 280px;
  flex: 1;
  max-width: 360px;
}

.class-filter-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1rem;
  margin: 0;
}

.class-filter-hint {
  font-weight: 400;
  color: #6c757d;
  font-size: 0.85rem;
}

.class-dropdown {
  position: relative;
}

.class-dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  padding: 12px 16px;
  border: 2px solid #FF8C42;
  border-radius: 12px;
  background: white;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.class-dropdown-trigger:hover {
  border-color: #FF7A29;
  box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.15);
}

.class-dropdown-trigger i {
  color: #FF8C42;
}

.selected-classes-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.class-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
}

.class-option:last-child {
  border-bottom: none;
}

.class-option:hover {
  background: #FFF5EF;
}

.class-option.selected {
  background: linear-gradient(135deg, #FFF5EF 0%, #FFE8D9 100%);
}

.class-checkbox {
  width: 22px;
  height: 22px;
  border: 2px solid #d0d0d0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.class-option.selected .class-checkbox {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border-color: #FF8C42;
}

.class-checkbox i {
  color: white;
  font-size: 0.75rem;
}

.class-name {
  font-size: 0.95rem;
  color: #2c3e50;
}

.selected-classes-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.class-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.class-tag i {
  cursor: pointer;
  opacity: 0.8;
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .class-filter-group {
    max-width: none;
    width: 100%;
  }
}
</style>
